// /components/:widgetId/install: allowed domains (saved with the header's Save),
// the snippet to paste into the installer's website, and a test link.
import { useEffect, useMemo, useState } from "react";
import { Check, Copy, ExternalLink } from "lucide-react";
import { normaliseBranding } from "@widget";
import { Link } from "react-router-dom";
import { useApi, type TermsStatus } from "../../api";
import { config } from "../../config";
import { DomainsEditor } from "../../components/DomainsEditor";
import { TermsCard } from "../../components/TermsCard";
import { useSaveBar, useTabDraft, useWidget } from "./WidgetLayout";

// The widget loader (and our demo page) is served from widget.<stage domain>
const WIDGET_HOST = config.stage === "prod" ? "widget.xenquote.com" : "widget.dev.xenquote.com";
export function InstallPage() {
  const { widget, setWidget } = useWidget();
  const api = useApi();
  const saved = useMemo(() => widget.domains ?? [], [widget.domains]);
  // Kept by the layout, so unsaved edits survive switching tabs
  const [domains, setDomains, discardDraft] = useTabDraft<string[]>("install", saved);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // Estimate-method terms for THIS widget: null while loading
  const [terms, setTerms] = useState<TermsStatus | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);

  // A contact form gives no estimates: no estimate terms to accept before installing
  const contactForm = widget.branding?.layout.style === "contact";

  useEffect(() => {
    api
      .getTerms(widget.widgetId)
      .then(setTerms)
      .catch((e: Error) => setTermsError(e.message));
  }, [api, widget.widgetId]);




  const dirty = JSON.stringify(domains) !== JSON.stringify(saved);
  useSaveBar({
    dirty,
    saving,
    blocked: false,
    onSave: save,
    onDiscard: () => {
      setDomains(saved);
      setMessage(null);
    },
  });

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      setWidget(await api.updateWidget(widget.widgetId, { domains }));
      discardDraft();
      setMessage({ tone: "success", text: "Allowed domains saved." });
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  const floating = normaliseBranding(widget.branding).layout.mode === "floating";
  // Hosted widgets run on our page: no allowed websites and no snippet, just the link
  const hosted = widget.branding?.layout.style === "hosted";
  // Floating widgets don't need a placeholder <div>; inline ones render into it
  const snippet = [
    `<script src="https://${WIDGET_HOST}/v1/loader.js" data-site-key="${widget.siteKey}" async></script>`,
    ...(floating ? [] : [`<div id="xenquote-${widget.widgetId.slice(0, 8)}"></div>`]),
  ].join("\n");
  const isTestKey = widget.siteKey.startsWith("pk_test_");
  // What still stops the widget running (the public API refuses sessions until
  // these are set). Prices come from the API's own to-do list (widgets
  // validation.pricing_missing), which knows each product: heat pump sizes, a
  // solar estimate's base / per-panel / inverter prices, none for contact forms,
  // and none on a test key (it quotes with sample prices).
  const needsPrices = widget.statusMissing?.some((m) => m.key === "pricing" || m.key === "solarPricing") ?? false;
  const missing: { text: string; tab: string }[] = [
    ...(widget.notifyEmails?.length ? [] : [{ text: "a notification email, so enquiries have somewhere to go", tab: "notifications" }]),
    ...(needsPrices ? [{ text: "your prices", tab: "pricing" }] : []),
  ];
  const demoUrl = `https://${WIDGET_HOST}/demo/index.html?key=${encodeURIComponent(widget.siteKey)}`;

  function copy() {
    void navigator.clipboard?.writeText(snippet).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="install">
      {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}

      {/* Allowed domains: the widget only runs on these websites (not needed for hosted pages) */}
      {!hosted && (
      <section className="card">
        <h2>Allowed websites</h2>
        <p className="card-sub">
          The calculator only runs on these websites; anywhere else it stays hidden. Adding <code>installer.co.uk</code> also allows{" "}
          <code>www.installer.co.uk</code>. Sites must use https.
        </p>
        <DomainsEditor
          domains={domains}
          onChange={(next) => {
            setDomains(next);
            setMessage(null);
          }}
        />
      </section>
      )}

      {/* Estimate terms: must be accepted for each widget (per version) before
          the snippet, demo link and site key are shown */}
      {!contactForm && terms === null && !termsError && <p className="muted">Checking estimate terms…</p>}
      {!contactForm && termsError && <div className="flash flash-danger">{termsError}</div>}
      {!contactForm && terms && !terms.accepted && (
        <TermsCard
          widgetId={widget.widgetId}
          // Solar estimates accept their own terms (terms.ts SOLAR_TERMS)
          product={widget.product}
          intro="Before you can add this calculator to your website, please read and accept how its estimates are worked out."
          buttonText="Accept and show the install code"
          onAccepted={(status) => {
            setTerms(status);
            // Accepting changes the widget's status (and its to-do list)
            void api.getWidget(widget.widgetId).then(setWidget);
          }}
        />
      )}

      {(contactForm || terms?.accepted) && (
        <>
      {!contactForm && terms && (
        <p className="terms-accepted">
          <Check size={14} aria-hidden="true" /> Estimate terms accepted for this calculator by {terms.acceptance?.acceptedByEmail || "your company"} on{" "}
          {terms.acceptance ? new Date(terms.acceptance.acceptedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : ""}{" "}
          (version {terms.acceptance?.version}).
        </p>
      )}

      {hosted ? (
        <section className="card">
          <h2>Your quote page</h2>
          <p className="card-sub">This calculator runs on a page we host for you: nothing to install. Send customers the link or use the QR code.</p>
          {widget.hostedUrl && <code className="site-key">{widget.hostedUrl}</code>}
          <p>
            <Link to="../hosted" className="btn btn-primary">
              Link, QR code and page details
            </Link>
          </p>
        </section>
      ) : (
      <section className="card">
        <h2>Add the calculator to your website</h2>
        <p className="card-sub">
          {floating
            ? "Paste this just before the closing </body> tag. The floating button appears on every page that has it."
            : "Paste the <script> just before </body>, and put the <div> where you want the calculator to appear."}
        </p>
        <pre className="snippet">
          <code>{snippet}</code>
        </pre>
        <button type="button" className="btn btn-primary" onClick={copy}>
          {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />} {copied ? "Copied" : "Copy snippet"}
        </button>
      </section>
      )}

      {/* Every key can be previewed on our demo page without adding it above (test keys also on localhost) */}
      {!hosted && (
        <section className="card">
          <h2>{isTestKey ? "Test it" : "Preview it"}</h2>
          <p className="card-sub">
            {isTestKey ? (
              <>
                This is a test key, so it also works on our demo page and on <code>localhost</code>, without adding them above. It uses
                your saved branding and prices.
              </>
            ) : (
              <>
                See exactly how it looks and works on an example website, with your saved branding and prices, before it's on your
                own site. The preview is free and doesn't count as live.
              </>
            )}
          </p>
          {missing.length > 0 && (
            <p className="price-error">
              Before you can try it, add{" "}
              {missing.map((m, i) => (
                <span key={m.tab}>
                  {i > 0 && " and "}
                  <Link to={m.tab.startsWith("/") ? m.tab : `../${m.tab}`}>{m.text}</Link>
                </span>
              ))}
              .
            </p>
          )}
          {/* Greyed out until the widget can actually run */}
          {missing.length > 0 ? (
            <button type="button" className="btn btn-secondary" disabled>
              <ExternalLink size={16} aria-hidden="true" /> Open the demo page
            </button>
          ) : (
            <a className="btn btn-secondary" href={demoUrl} target="_blank" rel="noreferrer">
              <ExternalLink size={16} aria-hidden="true" /> Open the demo page
            </a>
          )}
        </section>
      )}

      <section className="card">
        <h2>Site key</h2>
        <p className="card-sub">Identifies this calculator. It's public by design: it only works on the websites you allow.</p>
        <code className="site-key">{widget.siteKey}</code>
      </section>
        </>
      )}
    </div>
  );
}
